import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, FileText, DollarSign, Calendar, Download, Edit, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteManager = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfDownloads, setPdfDownloads] = useState({});

  useEffect(() => {
    loadQuotes();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredQuotes(quotes);
    } else {
      const filtered = quotes.filter(quote =>
        quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.client_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQuotes(filtered);
    }
  }, [searchTerm, quotes]);

  const loadQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      const sortedQuotes = response.data.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      setQuotes(sortedQuotes);
      setFilteredQuotes(sortedQuotes);
    } catch (error) {
      console.error('Failed to load quotes', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (quoteId, quoteNumber) => {
    try {
      toast.info('Generating PDF...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in again');
        return;
      }
      
      const pdfUrl = `${API}/quotes/${quoteId}/pdf?token=${encodeURIComponent(token)}&timestamp=${Date.now()}`;
      
      const newTab = window.open(pdfUrl, '_blank');
      
      if (!newTab) {
        toast.warn('Popup blocked! Click the PDF link below.');
      } else {
        toast.success('PDF opened in new tab!');
      }
      
      setPdfDownloads(prev => ({
        ...prev,
        [quoteId]: { url: pdfUrl, filename: `quote_${quoteNumber}.pdf` }
      }));
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleEmailQuote = async (quoteId, clientEmail) => {
    if (!clientEmail) {
      toast.error('Client email address not found');
      return;
    }

    try {
      toast.info('Sending email...');
      await axios.post(`${API}/quotes/${quoteId}/email`);
      toast.success(`Quote emailed successfully to ${clientEmail}`);
    } catch (error) {
      console.error('Email error:', error);
      if (error.response?.status === 400) {
        toast.error('Email not configured. Please set up SMTP in Company Settings.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send email');
      }
    }
  };

  const handleEditQuote = (quoteId) => {
    navigate(`/quote-builder?load=${quoteId}`);
  };

  const handleCreateNew = () => {
    navigate('/quote-builder');
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
              Quote Manager
            </h1>
            <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
              Manage all quotes and follow up with customers
            </p>
          </div>
          <Button
            onClick={handleCreateNew}
            className="font-mono uppercase tracking-wider"
            data-testid="create-new-quote"
          >
            <Plus size={20} className="mr-2" />
            New Quote
          </Button>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
            <Input
              type="text"
              placeholder="Search by quote number, client name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 font-mono"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Total Quotes</p>
                  <p className="text-2xl font-bold font-mono text-primary">{quotes.length}</p>
                </div>
                <FileText className="text-primary" size={24} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Total Value</p>
                  <p className="text-2xl font-bold font-mono text-green-500">
                    ${quotes.reduce((sum, q) => sum + q.total, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <DollarSign className="text-green-500" size={24} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Showing</p>
                  <p className="text-2xl font-bold font-mono text-blue-500">{filteredQuotes.length}</p>
                </div>
                <Search className="text-blue-500" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="space-y-4">
        {filteredQuotes.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto mb-4 text-muted-foreground" size={48} />
              <p className="text-lg font-mono text-muted-foreground">
                {searchTerm ? 'No quotes found matching your search' : 'No quotes yet'}
              </p>
              {!searchTerm && (
                <Button onClick={handleCreateNew} className="mt-4 font-mono uppercase">
                  <Plus size={20} className="mr-2" />
                  Create First Quote
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredQuotes.map((quote) => (
            <Card key={quote.id} className="border-border hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold font-mono text-foreground mb-1">
                            {quote.quote_number}
                          </h3>
                          {quote.capacity_warning && (
                            <span className="px-2 py-1 text-xs font-bold bg-red-500 text-white rounded uppercase">
                              Out of Capacity
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground font-mono">
                          {quote.client_name}
                        </p>
                        {quote.labour_hours > 0 && (
                          <p className="text-xs text-muted-foreground font-mono mt-1">
                            Labour: {quote.labour_hours}h
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-mono text-primary">
                          ${quote.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-muted-foreground" />
                        <span className="font-mono text-muted-foreground">{formatDate(quote.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-muted-foreground" />
                        <span className="font-mono text-muted-foreground text-xs">{quote.client_email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-muted-foreground" />
                        <span className="font-mono text-muted-foreground">{quote.line_items?.length || 0} items</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => handleEditQuote(quote.id)}
                      size="sm"
                      variant="outline"
                      className="font-mono uppercase text-xs"
                      data-testid={`edit-quote-${quote.id}`}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>

                    <Button
                      onClick={() => handleDownloadPDF(quote.id, quote.quote_number)}
                      size="sm"
                      variant="outline"
                      className="font-mono uppercase text-xs"
                      data-testid={`download-pdf-${quote.id}`}
                    >
                      <Download size={14} className="mr-1" />
                      View PDF
                    </Button>

                    <Button
                      onClick={() => handleEmailQuote(quote.id, quote.client_email)}
                      size="sm"
                      variant="outline"
                      className="font-mono uppercase text-xs"
                      data-testid={`email-quote-${quote.id}`}
                    >
                      <Mail size={14} className="mr-1" />
                      Email Client
                    </Button>

                    {pdfDownloads[quote.id] && (
                      <a
                        href={pdfDownloads[quote.id].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm font-mono uppercase text-xs transition-colors"
                        data-testid={`pdf-link-${quote.id}`}
                      >
                        <Download size={14} />
                        Open PDF
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default QuoteManager;
